/**
 * Shifts the mobile WhatsApp button up when the sticky add-to-cart bar is
 * visible on product pages, then returns it when the bar hides.
 */
(() => {
  const MOBILE_MQ = '(max-width: 749px)';
  const BUTTON_SELECTOR = '.wbs-whatsapp-chatbot-button--mobile';
  const STICKY_BAR_SELECTOR = '.sticky-add-to-cart__bar';
  const STICKY_HOST_SELECTOR = 'sticky-add-to-cart';
  const OFFSET_VAR = '--wbs-whatsapp-sticky-offset';

  /** @type {HTMLElement | null} */
  let button = null;
  /** @type {HTMLElement | null} */
  let stickyBar = null;
  /** @type {HTMLElement | null} */
  let stickyHost = null;
  /** @type {MutationObserver | null} */
  let attributeObserver = null;
  /** @type {ResizeObserver | null} */
  let resizeObserver = null;
  /** @type {MutationObserver | null} */
  let documentObserver = null;
  let documentSyncScheduled = false;
  const mediaQuery = window.matchMedia(MOBILE_MQ);

  /**
   * @returns {number}
   */
  function getStickyOffset() {
    if (!mediaQuery.matches || !stickyBar) return 0;
    if (stickyBar.dataset.stuck !== 'true') return 0;
    if (stickyHost?.dataset.variantAvailable === 'false') return 0;

    const height = stickyBar.offsetHeight;
    return height > 0 ? height : 0;
  }

  function applyOffset() {
    if (!button) return;
    button.style.setProperty(OFFSET_VAR, `${getStickyOffset()}px`);
  }

  function unbindStickyBar() {
    attributeObserver?.disconnect();
    attributeObserver = null;
    resizeObserver?.disconnect();
    resizeObserver = null;
    stickyBar = null;
    stickyHost = null;
  }

  /**
   * @param {HTMLElement | null} bar
   */
  function bindStickyBar(bar) {
    if (bar === stickyBar) {
      applyOffset();
      return;
    }

    unbindStickyBar();
    stickyBar = bar;
    stickyHost = bar?.closest(STICKY_HOST_SELECTOR) ?? null;

    if (!bar) {
      applyOffset();
      return;
    }

    attributeObserver = new MutationObserver(applyOffset);
    attributeObserver.observe(bar, {
      attributes: true,
      attributeFilter: ['data-stuck'],
    });

    if (stickyHost) {
      attributeObserver.observe(stickyHost, {
        attributes: true,
        attributeFilter: ['data-variant-available'],
      });
    }

    resizeObserver = new ResizeObserver(applyOffset);
    resizeObserver.observe(bar);
    applyOffset();
  }

  function syncStickyBar() {
    bindStickyBar(/** @type {HTMLElement | null} */ (document.querySelector(STICKY_BAR_SELECTOR)));
  }

  function scheduleStickyBarSync() {
    if (documentSyncScheduled) return;
    documentSyncScheduled = true;
    requestAnimationFrame(() => {
      documentSyncScheduled = false;
      const nextBar = /** @type {HTMLElement | null} */ (document.querySelector(STICKY_BAR_SELECTOR));
      if (nextBar !== stickyBar) syncStickyBar();
    });
  }

  function init() {
    button = /** @type {HTMLElement | null} */ (document.querySelector(BUTTON_SELECTOR));
    if (!button) return;

    syncStickyBar();

    documentObserver = new MutationObserver(scheduleStickyBarSync);
    documentObserver.observe(document.body, { childList: true, subtree: true });

    mediaQuery.addEventListener('change', applyOffset);

    customElements.whenDefined('sticky-add-to-cart').then(syncStickyBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
